/**
 * Company Screening Component
 *
 * Screening results tab for company detail page.
 * Shows company and UBO screening history, results, and hit details.
 */

import React, { useState } from 'react';
import {
  Shield,
  CheckCircle,
  AlertTriangle,
  Clock,
  Users,
  Building2,
  Calendar,
  Search,
  ChevronDown,
  ChevronRight,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import { useScreenCompany, useScreeningChecks } from '../../hooks';

const SCREENING_STATUS = {
  clear: { label: 'Clear', color: '#22c55e', icon: CheckCircle },
  hits_pending: { label: 'Hits Pending Review', color: '#f59e0b', icon: AlertTriangle },
  hits_resolved: { label: 'Hits Resolved', color: 'var(--accent-primary)', icon: CheckCircle },
  error: { label: 'Screening Error', color: '#ef4444', icon: XCircle },
  pending: { label: 'Not Screened', color: '#8b919e', icon: Clock },
};

const HIT_TYPE_CONFIG = {
  sanctions: { label: 'Sanctions', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
  pep: { label: 'PEP', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
  adverse_media: { label: 'Adverse Media', color: '#f97316', bg: 'rgba(249, 115, 22, 0.1)' },
  unknown: { label: 'Match', color: '#8b919e', bg: 'rgba(139, 145, 158, 0.1)' },
};

function ConfidenceBar({ confidence }) {
  const pct = Math.round(confidence);
  const color = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#8b919e';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 60, height: 4, background: 'var(--bg-primary, #0a0b0e)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2 }} />
      </div>
      <span style={{ fontSize: 12, color, fontWeight: 500 }}>{pct}%</span>
    </div>
  );
}

function HitCard({ hit }) {
  const [expanded, setExpanded] = useState(false);
  const hitType = HIT_TYPE_CONFIG[hit.hit_type] || HIT_TYPE_CONFIG.unknown;

  return (
    <div className="hit-card">
      <div className="hit-card-header" onClick={() => setExpanded(!expanded)} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          {expanded ? <ChevronDown size={14} style={{ flexShrink: 0, color: 'var(--text-secondary)' }} /> : <ChevronRight size={14} style={{ flexShrink: 0, color: 'var(--text-secondary)' }} />}
          <span className="hit-type-badge" style={{ color: hitType.color, background: hitType.bg }}>
            {hitType.label}
          </span>
          <span style={{ fontSize: 14, color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {hit.matched_name}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
          <ConfidenceBar confidence={hit.confidence} />
          <span className="hit-resolution" style={{ color: hit.resolution_status === 'pending' ? '#f59e0b' : '#22c55e' }}>
            {hit.resolution_status === 'pending' ? 'Pending' : hit.resolution_status}
          </span>
        </div>
      </div>
      {expanded && (
        <div className="hit-card-details">
          <div className="hit-detail-row">
            <span className="hit-detail-label">Source</span>
            <span className="hit-detail-value">{hit.list_source || 'OpenSanctions'}</span>
          </div>
          <div className="hit-detail-row">
            <span className="hit-detail-label">Confidence</span>
            <span className="hit-detail-value">{Math.round(hit.confidence)}%</span>
          </div>
          {hit.match_data && (
            <>
              {hit.match_data.dataset && (
                <div className="hit-detail-row">
                  <span className="hit-detail-label">Dataset</span>
                  <span className="hit-detail-value">{hit.match_data.dataset}</span>
                </div>
              )}
              {hit.match_data.topics && (
                <div className="hit-detail-row">
                  <span className="hit-detail-label">Categories</span>
                  <span className="hit-detail-value">
                    {Array.isArray(hit.match_data.topics) ? hit.match_data.topics.join(', ') : String(hit.match_data.topics)}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function CompanyScreening({ companyId, company }) {
  const screenMutation = useScreenCompany();

  // Fetch screening checks for this company
  const { data: checksData } = useScreeningChecks(
    { company_id: companyId },
    { enabled: !!company.last_screened_at }
  );
  const checks = checksData?.items || [];

  const screeningStatus = SCREENING_STATUS[company.screening_status] || SCREENING_STATUS.pending;
  const ScreeningIcon = screeningStatus.icon;

  const handleRunScreening = async () => {
    try {
      await screenMutation.mutateAsync(companyId);
    } catch (err) {
      console.error('Failed to run screening:', err);
    }
  };

  const companyFlags = company.flags || [];
  const ubos = company.beneficial_owners || [];
  const ubosWithHits = ubos.filter((ubo) => ubo.screening_status === 'hits');

  // Collect all hits from checks
  const allHits = checks.flatMap((check) => check.hits || []);

  return (
    <div className="company-screening">
      <style>{`
        .company-screening {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .screening-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
        }

        .screening-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .screening-title svg {
          width: 20px;
          height: 20px;
          color: var(--accent-primary, var(--accent-primary));
        }

        .run-screening-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: var(--accent-primary, var(--accent-primary));
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.15s;
          font-family: inherit;
        }

        .run-screening-btn:hover {
          background: var(--accent-hover, #5558e3);
        }

        .run-screening-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .run-screening-btn svg {
          width: 14px;
          height: 14px;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .screening-in-progress {
          animation: pulse 1.5s ease-in-out infinite;
        }

        .screening-summary {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
        }

        .summary-card {
          background: var(--bg-tertiary, #1a1d24);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 8px;
          padding: 16px;
        }

        .summary-card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
        }

        .summary-card-header svg {
          width: 16px;
          height: 16px;
          color: var(--text-secondary, #8b919e);
        }

        .summary-card-title {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary, #8b919e);
          margin: 0;
        }

        .summary-status {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 500;
        }

        .status-badge svg {
          width: 14px;
          height: 14px;
        }

        .summary-date {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .summary-date svg {
          width: 12px;
          height: 12px;
        }

        .flags-section {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid var(--border-primary, #23262f);
        }

        .flags-title {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary, #8b919e);
          margin: 0 0 8px;
        }

        .flags-list {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .flag-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
        }

        .flag-badge svg {
          width: 10px;
          height: 10px;
        }

        /* Hit Detail Cards */
        .hits-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .hits-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .hits-section-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .hit-count-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
        }

        .hit-card {
          background: var(--bg-tertiary, #1a1d24);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 8px;
          overflow: hidden;
          transition: border-color 0.15s;
        }

        .hit-card:hover {
          border-color: var(--text-secondary, #4a4e57);
        }

        .hit-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          gap: 12px;
        }

        .hit-type-badge {
          display: inline-flex;
          align-items: center;
          padding: 3px 8px;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          flex-shrink: 0;
        }

        .hit-resolution {
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .hit-card-details {
          padding: 0 16px 16px;
          border-top: 1px solid var(--border-primary, #23262f);
          padding-top: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .hit-detail-row {
          display: flex;
          align-items: baseline;
          gap: 12px;
        }

        .hit-detail-label {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
          min-width: 90px;
          flex-shrink: 0;
        }

        .hit-detail-value {
          font-size: 13px;
          color: var(--text-primary, #f0f2f5);
        }

        /* Error State */
        .screening-error-banner {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: rgba(239, 68, 68, 0.08);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 8px;
        }

        .screening-error-banner svg {
          width: 20px;
          height: 20px;
          color: #ef4444;
          flex-shrink: 0;
        }

        .screening-error-text {
          flex: 1;
        }

        .screening-error-text h4 {
          margin: 0 0 4px;
          font-size: 14px;
          color: #ef4444;
        }

        .screening-error-text p {
          margin: 0;
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
        }

        .retry-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 6px;
          color: #ef4444;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          font-family: inherit;
          transition: background 0.15s;
          flex-shrink: 0;
        }

        .retry-btn:hover {
          background: rgba(239, 68, 68, 0.2);
        }

        .retry-btn svg {
          width: 14px;
          height: 14px;
        }

        .ubo-screening-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .ubo-screening-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary, #f0f2f5);
          margin: 0;
        }

        .ubo-screening-card {
          background: var(--bg-tertiary, #1a1d24);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 8px;
          padding: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .ubo-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .ubo-name {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary, #f0f2f5);
        }

        .ubo-role {
          font-size: 12px;
          color: var(--text-secondary, #8b919e);
        }

        .ubo-screening-status {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .no-screening-message {
          padding: 40px;
          text-align: center;
          background: var(--bg-tertiary, #1a1d24);
          border: 1px solid var(--border-primary, #23262f);
          border-radius: 8px;
        }

        .no-screening-message svg {
          width: 40px;
          height: 40px;
          color: var(--text-secondary, #8b919e);
          margin-bottom: 12px;
        }

        .no-screening-message h4 {
          margin: 0 0 8px;
          font-size: 14px;
          color: var(--text-primary, #f0f2f5);
        }

        .no-screening-message p {
          margin: 0;
          font-size: 13px;
          color: var(--text-secondary, #8b919e);
        }
      `}</style>

      <div className="screening-header">
        <h3 className="screening-title">
          <Shield />
          Screening Results
        </h3>
        <button
          className={`run-screening-btn ${screenMutation.isPending ? 'screening-in-progress' : ''}`}
          onClick={handleRunScreening}
          disabled={screenMutation.isPending}
        >
          {screenMutation.isPending ? <RefreshCw /> : <Search />}
          {screenMutation.isPending ? 'Screening...' : 'Run Screening'}
        </button>
      </div>

      {/* Error State */}
      {company.screening_status === 'error' && (
        <div className="screening-error-banner">
          <XCircle />
          <div className="screening-error-text">
            <h4>Screening Failed</h4>
            <p>The last screening attempt encountered an error. Try running screening again.</p>
          </div>
          <button className="retry-btn" onClick={handleRunScreening} disabled={screenMutation.isPending}>
            <RefreshCw />
            Retry
          </button>
        </div>
      )}

      {!company.last_screened_at && company.screening_status !== 'error' ? (
        <div className="no-screening-message">
          <Shield />
          <h4>No screening performed</h4>
          <p>Run screening to check company and UBOs against sanctions and PEP lists.</p>
        </div>
      ) : company.last_screened_at && (
        <>
          <div className="screening-summary">
            <div className="summary-card">
              <div className="summary-card-header">
                <Building2 />
                <h4 className="summary-card-title">Company Screening</h4>
              </div>
              <div className="summary-status">
                <span
                  className="status-badge"
                  style={{
                    color: screeningStatus.color,
                    background: `${screeningStatus.color}15`,
                  }}
                >
                  <ScreeningIcon />
                  {screeningStatus.label}
                </span>
              </div>
              <div className="summary-date">
                <Calendar />
                Last screened: {new Date(company.last_screened_at).toLocaleDateString()}
              </div>

              {companyFlags.length > 0 && (
                <div className="flags-section">
                  <h5 className="flags-title">Risk Flags</h5>
                  <div className="flags-list">
                    {companyFlags.map((flag) => (
                      <span key={flag} className="flag-badge">
                        <AlertTriangle />
                        {flag.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="summary-card">
              <div className="summary-card-header">
                <Users />
                <h4 className="summary-card-title">UBO Screening</h4>
              </div>
              <div className="summary-status">
                {ubosWithHits.length > 0 ? (
                  <span
                    className="status-badge"
                    style={{
                      color: '#ef4444',
                      background: 'rgba(239, 68, 68, 0.1)',
                    }}
                  >
                    <AlertTriangle />
                    {ubosWithHits.length} UBO{ubosWithHits.length !== 1 ? 's' : ''} with hits
                  </span>
                ) : ubos.some((ubo) => ubo.screening_status === 'clear') ? (
                  <span
                    className="status-badge"
                    style={{
                      color: '#22c55e',
                      background: 'rgba(34, 197, 94, 0.1)',
                    }}
                  >
                    <CheckCircle />
                    All Clear
                  </span>
                ) : (
                  <span
                    className="status-badge"
                    style={{
                      color: '#8b919e',
                      background: 'rgba(139, 145, 158, 0.1)',
                    }}
                  >
                    <Clock />
                    Pending
                  </span>
                )}
              </div>
              <div className="summary-date">
                <Users />
                {ubos.length} beneficial owner{ubos.length !== 1 ? 's' : ''} tracked
              </div>
            </div>
          </div>

          {/* Screening Hit Details */}
          {allHits.length > 0 && (
            <div className="hits-section">
              <div className="hits-section-header">
                <h4 className="hits-section-title">
                  Screening Hits
                  <span className="hit-count-badge">{allHits.length}</span>
                </h4>
              </div>
              {allHits.map((hit) => (
                <HitCard key={hit.id} hit={hit} />
              ))}
            </div>
          )}

          {ubos.length > 0 && (
            <div className="ubo-screening-list">
              <h4 className="ubo-screening-title">Beneficial Owner Details</h4>

              {ubos.map((ubo) => {
                const uboStatus = SCREENING_STATUS[ubo.screening_status] || SCREENING_STATUS.pending;
                const UboIcon = uboStatus.icon;

                return (
                  <div key={ubo.id} className="ubo-screening-card">
                    <div className="ubo-info">
                      <span className="ubo-name">{ubo.full_name}</span>
                      {(ubo.role_title || ubo.ownership_percentage) && (
                        <span className="ubo-role">
                          {ubo.role_title}
                          {ubo.ownership_percentage && `${ubo.role_title ? ' \u2022 ' : ''}${ubo.ownership_percentage}%`}
                        </span>
                      )}
                    </div>

                    <div className="ubo-screening-status">
                      <span
                        className="status-badge"
                        style={{
                          color: uboStatus.color,
                          background: `${uboStatus.color}15`,
                        }}
                      >
                        <UboIcon />
                        {uboStatus.label}
                      </span>

                      {ubo.flags?.length > 0 && (
                        <div className="flags-list">
                          {ubo.flags.map((flag) => (
                            <span key={flag} className="flag-badge">
                              {flag.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
